import { useCallback } from 'react';

import {
  useUpgrade3dMutation,
  useMeshStatusQuery,
  type AvatarMesh,
} from '@/services/avatarApi';

function isTerminal(m?: AvatarMesh): boolean {
  if (!m) return true;
  if (m.status === 'ready') return true;
  if (m.status === 'failed' && m.attempts >= 3) return true;
  return false;
}

export interface MeshLifecycle {
  mesh?: AvatarMesh;
  trigger: (force?: boolean, provider?: 'tripo' | 'meshy' | 'rodin-fal') => Promise<unknown>;
  isPending: boolean;
}

export function useMeshLifecycle(avatarId: string, initialMesh?: AvatarMesh): MeshLifecycle {
  const [upgrade, { isLoading: isMutating }] = useUpgrade3dMutation();
  // `skip` is re-evaluated every render: once the polled mesh reaches a
  // terminal state, polling halts automatically on the next render.
  const skipPoll = isTerminal(initialMesh) || !avatarId;
  const { data: polledMesh } = useMeshStatusQuery(avatarId, {
    pollingInterval: 5000,
    skip: skipPoll,
  });
  const mesh = polledMesh ?? initialMesh;
  const trigger = useCallback(
    (force = false, provider?: 'tripo' | 'meshy' | 'rodin-fal') =>
      upgrade({ avatarId, force, ...(provider ? { provider } : {}) }).unwrap(),
    [avatarId, upgrade],
  );
  return { mesh, trigger, isPending: isMutating };
}
